import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  ImageUploadError,
  prepareAttachment,
} from '../lib/image-upload';
import { haptic } from '../lib/telegram';
import type { CustomerOrderDetails, OrderAttachment } from '../types';
import { DocumentTile, isDocumentAttachment } from './order-files';
import { ZoomableImage } from './zoomable-image';
import { Button, Card, ErrorNotice } from './ui';

export const MAX_REFERENCES = 3;

/**
 * Референсы заказчика. Раньше показать художнику картинку можно было только
 * ссылкой в заявке или сообщением в переписке — и то и другое уезжает вверх
 * ленты через неделю. Здесь они лежат при заказе и никуда не деваются.
 */
export function OrderReferences({ order }: { order: CustomerOrderDetails }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = (updated: CustomerOrderDetails) => {
    haptic('success');
    queryClient.setQueryData(['customer', 'order', order.id], updated);
  };

  const remove = useMutation({
    mutationFn: (attachmentId: string) =>
      api<CustomerOrderDetails>(`/customer/orders/${order.id}/references/${attachmentId}`, {
        method: 'DELETE',
      }),
    onSuccess: refresh,
  });

  const add = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    setPending(true);
    setError(null);

    try {
      refresh(
        await api<CustomerOrderDetails>(`/customer/orders/${order.id}/references`, {
          method: 'POST',
          body: { data: await prepareAttachment(file), fileName: file.name },
        }),
      );
    } catch (failure) {
      setError(
        failure instanceof ImageUploadError || failure instanceof ApiError
          ? failure.message
          : 'Не удалось загрузить файл',
      );
    } finally {
      setPending(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <>
      <div className="section-heading">
        <h2>Референсы</h2>
        <span className="meta">
          {order.references.length} из {MAX_REFERENCES}
        </span>
      </div>

      <Card className="stack stack--compact">
        {order.references.length === 0 && (
          <p className="muted">
            Покажите художнику, к чему стремитесь: позы, палитра, стиль. До трёх картинок.
          </p>
        )}

        {order.references.length > 0 && (
          <div className="reference-grid">
            {order.references.map((reference) => (
              <figure key={reference.id} className="reference-tile">
                <ReferencePreview reference={reference} />
                {order.canAddReferences && (
                  <button
                    type="button"
                    className="reference-tile__remove"
                    aria-label={`Убрать ${reference.fileName}`}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(reference.id)}
                  >
                    ×
                  </button>
                )}
              </figure>
            ))}
          </div>
        )}

        {error && <span className="field__error">{error}</span>}
        {remove.error && <ErrorNotice error={remove.error} />}

        {order.canAddReferences && (
          <Button variant="secondary" disabled={pending} onClick={() => input.current?.click()}>
            {pending ? 'Загружаем…' : '+ Добавить референс'}
          </Button>
        )}

        <input
          ref={input}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES}
          hidden
          onChange={(event) => void add(event.target.files)}
        />
      </Card>
    </>
  );
}

/**
 * Референсы художника: только показать. Правит их заказчик — это его картинки,
 * и подменять их за него нельзя.
 */
export function ArtistReferences({ references }: { references: OrderAttachment[] }) {
  if (references.length === 0) {
    return null;
  }

  return (
    <>
      <div className="section-heading">
        <h2>Референсы заказчика</h2>
        <span className="meta">{references.length}</span>
      </div>
      <Card>
        <div className="reference-grid">
          {references.map((reference) => (
            <figure key={reference.id} className="reference-tile">
              <ReferencePreview reference={reference} />
            </figure>
          ))}
        </div>
      </Card>
    </>
  );
}

function ReferencePreview({ reference }: { reference: OrderAttachment }) {
  if (!reference.url || isDocumentAttachment(reference.mimeType)) {
    return <DocumentTile fileName={reference.fileName} sizeBytes={reference.sizeBytes} />;
  }

  return <ZoomableImage url={reference.url} alt={reference.fileName} />;
}
